import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { compareDesc, format } from 'date-fns';
import { Observable, OperatorFunction, pipe } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { CommitmentEntity } from '../../coaching-base/config';
import {
    CommitmentDto,
    CommitmentFocusParameters,
    CommitmentFocusSummarySetDto,
    CommitmentKpi,
    CommitmentSummaryListItem,
} from '../../coaching-base/general';
import { PerformerOa } from '../../coaching-detail/pillar-one/performer-oa.model';
import { ObjectDateTransformer } from '../../coaching-insight/coaching-insight.model';
import { EvaluationDetails } from '../../coaching-observation/observation-tag-group/observation.focus.summary.component/evaluation-details.model';
import { QueueItem } from '../../coaching-tags/models/queue.coaching.model';
import {
    catchErrorLogRet,
    mapDelete,
    mapList,
    mapRetrieve,
} from '../../common/rest.operators';
import { CommitmentSummaryItemTransformer } from '../coaching-commitment.model';
import { CommitmentDtoTransformer } from '../commitment/commitment-dto.transformer';
import { CommitmentGoalHistory } from '../models/commitment-goal-history.model';
import { FocusKPI } from '../models/commitment.model';
import { GoalEventDataPoint } from '../models/goal-event-data-point.model';

@Injectable()
export class CommitmentService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    retrieve<T>(
        project: () => {
            commitmentId: number;
            goalsStartDate?: Date;
            goalsEndDate?: Date;
        },
    ): OperatorFunction<T, CommitmentDto> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.goalsStartDate != null) {
                    params = params.set(
                        'goalsStartDate',
                        format(args.goalsStartDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.goalsEndDate != null) {
                    params = params.set(
                        'goalsEndDate',
                        format(args.goalsEndDate, 'yyyy-MM-dd'),
                    );
                }
                return this._http
                    .get(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/${args.commitmentId}`,
                        { params },
                    )
                    .pipe(mapRetrieve(new CommitmentDtoTransformer()));
            }),
            catchErrorLogRet(null),
        );
    }

    /// /////////////////////////////////////////////////

    // TODO post-refactor. from coaching.config.service>initializeConfig
    retrieveCommitmentConfig<T>(): OperatorFunction<T, CommitmentEntity> {
        return pipe(
            switchMap(() =>
                this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/config`,
                ),
            ),
            mapRetrieve(),
            map((ret) => JSON.parse(ret)),
            catchErrorLogRet({}),
        );
    }

    /// /////////////////////////////////////////////////

    listAvailableQueues<T, O extends { userId: number; effectiveDate?: Date }>(
        project: (value: T, index: number) => O,
    ): OperatorFunction<T, QueueItem[]> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params: HttpParams;
                if (args.effectiveDate) {
                    params = new HttpParams().set(
                        'effectiveDate',
                        args.effectiveDate.toJSON(),
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.USER_PROFILE}/UserProfile/${args.userId}/availableQueues`,
                    { params },
                );
            }),
            mapList(),
            catchErrorLogRet([]),
        );
    }

    /// /////////////////////////////////////////////////

    listFocuses<T>(
        project: (value: T, index: number) => CommitmentFocusParameters,
    ): OperatorFunction<T, CommitmentFocusSummarySetDto> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params = new HttpParams().set(
                    'userId',
                    args.userId.toString(),
                );
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-return-assign
                args.statusIds &&
                    args.statusIds.forEach(
                        (e) =>
                            (params = params.append('statusIds', e.toString())),
                    );
                if (args.recentlyActive) {
                    params = params.set(
                        'recentlyActive',
                        args.recentlyActive.toString(),
                    );
                }
                if (args.recentlyActiveDays) {
                    params = params.set(
                        'recentlyActiveDays',
                        args.recentlyActiveDays.toString(),
                    );
                }
                if (args.recentlyClosed) {
                    params = params.set(
                        'recentlyClosed',
                        args.recentlyClosed.toString(),
                    );
                }
                if (args.recentlyClosedDays) {
                    params = params.set(
                        'recentlyClosedDays',
                        args.recentlyClosedDays.toString(),
                    );
                }
                if (args.teamFocuses) {
                    params = params.set(
                        'teamFocuses',
                        args.teamFocuses.toString(),
                    );
                }
                if (args.teamRecentlyActiveDays) {
                    params = params.set(
                        'teamRecentlyActiveDays',
                        args.teamRecentlyActiveDays.toString(),
                    );
                }
                if (args.teamRecentlyClosedDays) {
                    params = params.set(
                        'teamRecentlyClosedDays',
                        args.teamRecentlyClosedDays.toString(),
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/focuses`,
                    { params },
                );
            }),
            mapRetrieve(
                new ObjectDateTransformer({
                    user: {
                        eventDate: null,
                    },
                    team: {
                        eventDate: null,
                    },
                }),
            ),
            catchErrorLogRet({}),
        );
    }

    delete<T>(
        project: (value: T, index: number) => number,
    ): OperatorFunction<T, boolean> {
        return pipe(
            switchMap((value, index) => {
                const commitmentId = project(value, index);
                return this._http.delete(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/${commitmentId}`,
                );
            }),
            mapDelete(),
        );
    }

    // TODO move
    retrievePerformerOa<
        T,
        O extends {
            performerId: number;
            periodType: number;
            startDate?: Date;
            endDate?: Date;
        },
    >(
        project: (value: T, index: number) => O,
    ): OperatorFunction<T, PerformerOa> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params = new HttpParams();
                if (args.startDate != null) {
                    params = params.set(
                        'startDate',
                        format(args.startDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.endDate != null) {
                    params = params.set(
                        'endDate',
                        format(args.endDate, 'yyyy-MM-dd'),
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/coaching/performeroa/${args.performerId}/${args.periodType}`,
                    { params },
                );
            }),
            mapRetrieve(),
            catchErrorLogRet({}),
        );
    }

    listSummary<
        T,
        O extends {
            userId: number;
            startDate?: Date;
            endDate?: Date;
        },
    >(
        project: (value: T, index: number) => O,
    ): OperatorFunction<T, CommitmentSummaryListItem[]> {
        return pipe(
            switchMap((value, index) => {
                const args = project(value, index);
                let params = new HttpParams().set(
                    'userId',
                    args.userId.toString(),
                );
                if (args.startDate != null) {
                    params = params.set(
                        'startDate',
                        format(args.startDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.endDate != null) {
                    params = params.set(
                        'endDate',
                        format(args.endDate, 'yyyy-MM-dd'),
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/summary`,
                    { params },
                );
            }),
            mapList(new CommitmentSummaryItemTransformer()),
            catchErrorLogRet([]),
        );
    }

    getOtherCommitmentsGoalsAndClosed(
        commitmentId: number,
        performerId: number,
    ): Observable<CommitmentKpi[]> {
        let params = new HttpParams();
        if (performerId)
            params = params.set('performerId', performerId.toString());

        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/${commitmentId}/othergoals`,
                { params },
            )
            .pipe(mapList(), catchErrorLogRet([]));
    }

    getFocusGoalKpis(
        behaviour: number,
        subBehaviour: number,
        performerId: number,
        observationIds: any[],
    ): Observable<FocusKPI[]> {
        let params = new HttpParams();
        if (behaviour) params = params.set('behaviourId', behaviour.toString());
        if (subBehaviour)
            params = params.set('subbehaviourId', subBehaviour.toString());
        if (performerId)
            params = params.set('performerId', performerId.toString());

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.COACHING}/focuses/kpis`,
                observationIds,
                { params },
            )
            .pipe(mapList(), catchErrorLogRet([]));
    }

    listGoalHistory(
        commitmentId: number,
        goalId: number,
    ): Observable<CommitmentGoalHistory[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/${commitmentId}/goals/${goalId}/history`,
            )
            .pipe(
                mapList(),
                map((ret: CommitmentGoalHistory[]) => {
                    const dateTransformer =
                        new ObjectDateTransformer<CommitmentGoalHistory>({
                            createdOn: '',
                            date: null,
                            expectedAchievementDate: 'L',
                        });
                    return ret
                        .map((e) => dateTransformer.transform(e))
                        .sort((a, b) => compareDesc(a.date, b.date));
                }),
                catchErrorLogRet([]),
            );
    }

    listHistory(commitmentId: number): Observable<GoalEventDataPoint[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/commitments/${commitmentId}/history`,
            )
            .pipe(
                mapList(),
                map((ret: GoalEventDataPoint[]) => {
                    const dateTransformer =
                        new ObjectDateTransformer<GoalEventDataPoint>({
                            date: '',
                            startTime: '',
                            endTime: '',
                        });
                    return ret
                        .map((e) => dateTransformer.transform(e))
                        .sort((a, b) => compareDesc(a.date, b.date));
                }),
                catchErrorLogRet([]),
            );
    }

    getEvaluationsDetails(
        performerId: number,
        behaviourId: number,
        subBehaviourId: number,
        observationId: number = null,
    ): Observable<EvaluationDetails[]> {
        let params = new HttpParams();
        if (performerId != null) {
            params = params.set('performerId', performerId.toString());
        }
        if (behaviourId != null) {
            params = params.set('behaviourId', behaviourId.toString());
        }
        if (subBehaviourId != null) {
            params = params.set('subBehaviourId', subBehaviourId.toString());
        }
        if (observationId != null) {
            params = params.set('observationId', observationId.toString());
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/focuses/evaluationsdetails`,
                { params },
            )
            .pipe(mapList(), catchErrorLogRet([]));
    }
}
